import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../interceptors";
import LoadingState from "../../components/shared/LoadingState";
import ErrorState from "../../components/shared/ErrorState";
import { toast } from "../../components/shared/toast";

// ── /candidate/profile ─────────────────────────────────────────────────────
// Backend: POST /job-posting/api/candidate-portal/profile/get returns
// ProfileDTO (candidateId, firstName, lastName, email, mobileNumber,
// currentLocation, profileCompletion).
//
// Read-only for this pass — the edit/PUT endpoint isn't built yet. The
// stub used to fake a save via alert(); replaced with an honest toast
// pointing at the future path. When the PUT lands this page upgrades
// without route changes.

export default function CandidateProfile() {
  const candidateId = useMemo(() => {
    const raw = localStorage.getItem("candidateId") || localStorage.getItem("userId");
    return raw ? Number(raw) : null;
  }, []);

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);

  const load = useCallback(() => {
    if (!candidateId) {
      setLoading(false);
      setError("Not signed in as a candidate");
      return;
    }
    setLoading(true);
    setError(null);
    setErrorStatus(null);
    axiosInstance
      .post("/job-posting/api/candidate-portal/profile/get", { candidateId })
      .then((r) => {
        if (r.data?.success) {
          setProfile(r.data.data || {});
        } else {
          setError(r.data?.message || "Couldn't load profile");
          setErrorStatus(500);
        }
      })
      .catch((e) => {
        setError(e?.message || "Couldn't load profile");
        setErrorStatus(e?.status || e?.response?.status || 500);
      })
      .finally(() => setLoading(false));
  }, [candidateId]);

  useEffect(() => {
    document.title = "My profile - Ample Hire";
    load();
  }, [load]);

  const editComingSoon = () => {
    toast.info("Profile editing is coming soon", {
      description: "Reach out to your recruiter if you need to update something urgently.",
    });
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto space-y-4">
        <Header />
        <div className="bg-white border border-subtle rounded-card p-6">
          <LoadingState variant="skeleton" shape="text" count={6} />
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="max-w-3xl mx-auto space-y-4">
        <Header />
        <ErrorState
          status={errorStatus}
          description={errorStatus === 403 ? undefined : error}
          retry={load}
        />
      </div>
    );
  }

  const p = profile || {};
  const pct = Math.max(0, Math.min(100, Math.round(p.profileCompletion || 0)));

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <Header onEdit={editComingSoon} />

      {/* Completion meter */}
      <section
        aria-label="Profile completion"
        className="rounded-card bg-white border border-subtle p-4"
      >
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-sm font-semibold text-vf-base">Profile completeness</div>
          <div className="text-xs text-vf-muted font-mono">{pct}%</div>
        </div>
        <div
          className="h-1.5 bg-vf-tint rounded-full overflow-hidden"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div className="h-full bg-vf-primary transition-all" style={{ width: `${pct}%` }} />
        </div>
        {pct < 100 && (
          <p className="text-xs text-vf-muted mt-2 m-0">
            A more complete profile helps recruiters match you with the right roles.
          </p>
        )}
      </section>

      {/* Fields — read-only for now */}
      <section
        aria-label="Profile fields"
        className="rounded-card bg-white border border-subtle p-5"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
          <Field label="First name"      value={p.firstName} />
          <Field label="Last name"       value={p.lastName} />
          <Field label="Email"           value={p.email} />
          <Field label="Mobile number"   value={p.mobileNumber} />
          <Field label="Current location" value={p.currentLocation} fullWidth />
        </div>

        <footer className="mt-5 pt-4 border-t border-subtle flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <p className="text-2xs text-vf-muted m-0">
            Profile editing isn't open yet. Contact your recruiter for urgent updates.
          </p>
          <button
            type="button"
            onClick={editComingSoon}
            className="text-xs px-3 py-1.5 rounded border border-subtle bg-white text-vf-base hover:bg-vf-tint focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary"
          >
            Request edit
          </button>
        </footer>
      </section>
    </div>
  );
}

function Header({ onEdit }) {
  return (
    <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
      <div>
        <h1 className="text-2xl font-semibold text-vf-base m-0">My profile</h1>
        <p className="text-sm text-vf-muted mt-1">
          What recruiters see when they look you up.
        </p>
      </div>
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          className="text-xs px-3 py-1.5 rounded bg-vf-primary text-white font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary"
        >
          Edit profile →
        </button>
      )}
    </header>
  );
}

function Field({ label, value, fullWidth }) {
  return (
    <div className={fullWidth ? "sm:col-span-2" : undefined}>
      <div className="text-2xs uppercase tracking-wide text-vf-muted font-semibold">{label}</div>
      <div className={`text-sm mt-1 ${value ? "text-vf-base" : "text-vf-muted italic"}`}>
        {value || "Not set"}
      </div>
    </div>
  );
}
